import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "What is a Kundli?",
    answer: "A Kundli (also known as Janam Kundali or Birth Chart) is a detailed astrological chart created based on your exact date, time, and place of birth. It maps the positions of planets, houses, and nakshatras at the moment of your birth, providing insights into your personality, life path, and destiny according to Vedic astrology."
  },
  {
    question: "What information do I need to generate my Kundli?",
    answer: "You need your exact date of birth, time of birth (as precise as possible), and place of birth. The location helps us calculate the correct latitude and longitude for accurate planetary positions. If you don't know your exact birth time, you can use an approximate time, but the results may be less precise."
  },
  {
    question: "What is Manglik Dosha?",
    answer: "Manglik Dosha (also called Mangal Dosha or Kuja Dosha) occurs when Mars is placed in certain houses (1st, 2nd, 4th, 7th, 8th, or 12th) in your birth chart. It is believed to affect marital harmony. However, there are many remedies and exceptions, and it's important to consult a qualified astrologer for a complete analysis."
  },
  {
    question: "What is a Nakshatra?",
    answer: "A Nakshatra is a lunar mansion or birth star in Vedic astrology. There are 27 Nakshatras, each spanning 13°20' of the zodiac. Your Nakshatra is determined by the Moon's position at your birth and plays a crucial role in personality analysis and compatibility matching."
  },
  {
    question: "How accurate is this Kundli?",
    answer: "Our Kundli Maker uses authentic Vedic astrology calculations based on established astronomical formulas. However, for detailed life predictions and important decisions, we recommend consulting a qualified Vedic astrologer who can provide personalized interpretations and guidance."
  },
  {
    question: "Can I use this Kundli for marriage matching?",
    answer: "Yes, the Kundli provides basic information like Nakshatra, Rashi, and Manglik status which are important for marriage compatibility. However, complete marriage matching (Ashta-Koota or Guna Milan) requires comparing two Kundlis and should ideally be done by an experienced astrologer."
  },
  {
    question: "What are the 12 houses in a Kundli?",
    answer: "The 12 houses (Bhavas) represent different life areas: 1st-Self, 2nd-Wealth, 3rd-Siblings, 4th-Home, 5th-Children, 6th-Health, 7th-Marriage, 8th-Longevity, 9th-Fortune, 10th-Career, 11th-Gains, 12th-Spirituality. Planets in these houses influence the respective life areas."
  },
  {
    question: "What is the difference between Sun Sign and Moon Sign?",
    answer: "Your Sun Sign (Rashi) represents your soul and core personality, while your Moon Sign represents your mind and emotional nature. In Vedic astrology, the Moon Sign is often considered more important for understanding your inner self and making predictions."
  },
  {
    question: "Is my data safe and private?",
    answer: "Yes, absolutely. All Kundli calculations are performed directly in your browser (client-side). We do not store your birth details on any server unless you explicitly choose to save them to your profile. You can download your Kundli for personal records."
  },
  {
    question: "Can I download or share my Kundli?",
    answer: "Yes! You can download your Kundli as a JSON file for your records, or share the basic details using your device's native share functionality. This makes it easy to share with family, astrologers, or potential matches."
  }
];

export function KundliFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleFAQ(index)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
              <ChevronDown 
                className={`size-5 text-gray-500 flex-shrink-0 transition-transform ${
                  openIndex === index ? 'transform rotate-180' : ''
                }`}
              />
            </button>
            {openIndex === index && (
              <div className="px-4 pb-4 text-gray-600">
                {faq.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
